import PageLayout from 'components/pages/PageLayout'
import { withApollo } from 'components/withApollo'
import Link from 'next/link'
import PageMeta from 'components/PageMeta'

const PrivacyPage = () => (
  <PageLayout staticPage>
    <PageMeta
      canonicalPath="/privacy"
      description="Best Food Trucks (BFT): Find & Book Food Trucks for Catering, Events, Weddings, and Office Lunches"
      title="Best Food Trucks | Privacy Policy"
    />
    <div className="prose lg:prose-lg my-10 text-justify">
      <h1>Privacy Statement</h1>

      <p>
        This statement covers customers of Best Food Trucks. If you are a food
        truck vendor, see the Vendor Privacy Statement at{' '}
        <a
          href="https://trucks.bestfoodtrucks.com/privacy"
          rel="noreferrer"
          target="_blank"
        >
          https://trucks.bestfoodtrucks.com/privacy
        </a>
        .
      </p>

      <div>
        <h2>Section 1 - What do we do with your information?</h2>
        <p>
          When you purchase something from our store, as part of the buying and
          selling process, we collect the personal information you give us such
          as your name, address and email address.
        </p>
        <p>
          When you browse our store, we also automatically receive your
          computer’s internet protocol (IP) address in order to provide us with
          information that helps us learn about your browser and operating
          system.
        </p>
        <p>
          Email marketing (if applicable): With your permission, we may send you
          emails about our store, new products and other updates.
        </p>
      </div>

      <div>
        <h2>Section 2 - Consent</h2>
        <p className="font-bold">How do you get my consent?</p>
        <p>
          When you provide us with personal information to complete a
          transaction, verify your credit card, place an order, arrange for a
          delivery or return a purchase, we imply that you consent to our
          collecting it and using it for that specific reason only.
        </p>
        <p>
          If we ask for your personal information for a secondary reason, like
          marketing, we will either ask you directly for your expressed consent,
          or provide you with an opportunity to say no.
        </p>
        <p className="font-bold">How do I withdraw my consent?</p>
        <p>
          If after you opt-in, you change your mind, you may withdraw your
          consent for us to contact you, for the continued collection, use or
          disclosure of your information, at anytime, by contacting us at{' '}
          <a href="mailto:support@bestfoodtrucks.com">
            support@bestfoodtrucks.com
          </a>{' '}
          or mailing us at:{' '}
          <span className="italic">
            Best Food Trucks Inc. 13488 Maxella Dr., #320, Marina Del Rey, CA,
            90292, United States
          </span>
        </p>
      </div>

      <div>
        <h2>Section 3 - Disclosure</h2>
        <p>
          We may disclose your personal information if we are required by law to
          do so or if you violate our{' '}
          <Link href="/terms" target="_blank">
            Terms of Service
          </Link>
          .
        </p>
      </div>

      <div>
        <h2>Section 4 - Best Food Trucks</h2>
        <p>
          Our store is hosted on Best Food Trucks Inc. They provide us with the
          online e-commerce platform that allows us to sell our products and
          services to you.
        </p>
        <p>
          Your data is stored through Best Food Trucks’s data storage, databases
          and the general Best Food Trucks application. They store your data on
          a secure server behind a firewall.
        </p>
        <p>Payment:</p>
        <p>
          If you choose a direct payment gateway to complete your purchase, then
          Best Food Trucks stores your credit card data. It is encrypted through
          the Payment Card Industry Data Security Standard (PCI-DSS). Your
          purchase transaction data is stored only as long as is necessary to
          complete your purchase transaction. After that is complete, your
          purchase transaction information is deleted.
        </p>
        <p>
          All direct payment gateways adhere to the standards set by PCI-DSS as
          managed by the PCI Security Standards Council, which is a joint effort
          of brands like Visa, MasterCard, American Express and Discover.
        </p>
        <p>
          PCI-DSS requirements help ensure the secure handling of credit card
          information by our store and its service providers.
        </p>
        <p>
          For more insight, you may also want to read Best Food Trucks’s Terms
          of Service here or Privacy Statement here.
        </p>
      </div>

      <div>
        <h2>Section 5 - Third-Party Services</h2>
        <p>
          In general, the third-party providers used by us will only collect,
          use and disclose your information to the extent necessary to allow
          them to perform the services they provide to us.
        </p>
        <p>
          However, certain third-party service providers, such as payment
          gateways and other payment transaction processors, have their own
          privacy policies in respect to the information we are required to
          provide to them for your purchase-related transactions.
        </p>
        <p>
          For these providers, we recommend that you read their privacy policies
          so you can understand the manner in which your personal information
          will be handled by these providers.
        </p>
        <p>
          In particular, remember that certain providers may be located in or
          have facilities that are located in a different jurisdiction than
          either you or us. So if you elect to proceed with a transaction that
          involves the services of a third-party service provider, then your
          information may become subject to the laws of the jurisdiction(s) in
          which that service provider or its facilities are located.
        </p>
        <p>
          As an example, if you are located in Canada and your transaction is
          processed by a payment gateway located in the United States, then your
          personal information used in completing that transaction may be
          subject to disclosure under United States legislation, including the
          Patriot Act.
        </p>
        <p>
          Once you leave our store’s website or are redirected to a third-party
          website or application, you are no longer governed by this Privacy
          Policy or our website’s Terms of Service.
        </p>
        <p>Links:</p>
        <p>
          When you click on links on our store, they may direct you away from
          our site. We are not responsible for the privacy practices of other
          sites and encourage you to read their privacy statements.
        </p>
      </div>

      <div>
        <h2>Section 6 - Security</h2>
        <p>
          To protect your personal information, we take reasonable precautions
          and follow industry best practices to make sure it is not
          inappropriately lost, misused, accessed, disclosed, altered or
          destroyed.
        </p>
        <p>
          If you provide us with your credit card information, the information
          is encrypted using secure socket layer technology (SSL) and stored
          with a AES-256 encryption. Although no method of transmission over the
          Internet or electronic storage is 100% secure, we follow all PCI-DSS
          requirements and implement additional generally accepted industry
          standards.
        </p>
      </div>

      <div>
        <h2>Section 7 - Cookies</h2>
        <p>Here is a list of cookies that we use.</p>
        <p>
          &quot;_bft_session_global&quot; - unique token, allows Best Food
          Trucks to store information about your session (which user is
          authenticated, etc). This cookie is required for the website to
          function and we do not recommend blocking it.
        </p>
        <p>
          &quot;_bft_web_tracking&quot; - stores how you arrived at Best Food
          Trucks (advertising click identifiers, campaign parameters, referring
          site and the pages you visited) so we can measure the effectiveness of
          our advertising. It expires after 30 days. If you opt out of tracking
          on the <Link href="/privacy-choices">Your Privacy Choices</Link> page,
          advertising identifiers are removed from this cookie.
        </p>
        <p>
          &quot;split&quot; - unique token, allows Best Food Trucks to conduct
          A/B testing and decide which version of the website a visitor is going
          to be shown to improve user experience.
        </p>
        <p>
          Cookies that Google Analytics is using. To see details or opt out
          please visit{' '}
          <a
            href="https://adssettings.google.com"
            rel="noreferrer"
            target="_blank"
          >
            https://adssettings.google.com
          </a>
          .
        </p>
        <p>
          Cookies that Facebook is using. To see details or opt out please visit{' '}
          <a
            href="https://www.facebook.com/about/ads"
            rel="noreferrer"
            target="_blank"
          >
            https://www.facebook.com/about/ads
          </a>
          .
        </p>
        <p>
          The LinkedIn Insight Tag uses cookies and similar technologies to help
          us measure the effectiveness of our LinkedIn advertising. For more
          information, see LinkedIn&apos;s{' '}
          <a
            href="https://www.linkedin.com/legal/cookie-policy"
            rel="noreferrer"
            target="_blank"
          >
            https://www.linkedin.com/legal/cookie-policy
          </a>
          .
        </p>
        <p>
          Microsoft Advertising (Bing) uses cookies and similar technologies to
          help us measure the effectiveness of our Bing advertising. For more
          information, see{' '}
          <a
            href="https://www.microsoft.com/en-us/privacy/privacystatement"
            rel="noreferrer"
            target="_blank"
          >
            https://www.microsoft.com/en-us/privacy/privacystatement
          </a>
          .
        </p>
        <p>
          The Apollo.io website visitor tracker uses cookies and similar
          technologies to help us understand which companies visit our website,
          which pages they view and how they engage with the site. For visitors
          in the United States, it may also help identify business contacts.
          Apollo.io may use information such as IP address and device or browser
          details to infer or enrich company and professional information for
          analytics, lead identification and marketing purposes. For more
          information, see Apollo.io&apos;s{' '}
          <a
            href="https://www.apollo.io/privacy-policy"
            rel="noreferrer"
            target="_blank"
          >
            https://www.apollo.io/privacy-policy
          </a>
          .
        </p>
      </div>

      <div>
        <h2>Section 8 - Privacy and Data Sharing</h2>
        <p>
          We use advertising and analytics services that may involve sharing
          certain information for cross-context behavioral advertising, which
          may be considered a &quot;sale&quot; or &quot;sharing&quot; of
          personal information under laws such as the California Consumer
          Privacy Act. Users located in the United States may opt out at any
          time on our <Link href="/privacy-choices">Your Privacy Choices</Link>{' '}
          page. We also honor the Global Privacy Control signal sent by your
          browser.
        </p>
        <p>
          For users located in the European Union, the European Economic Area,
          the United Kingdom, or Switzerland, we do not sell or share personal
          information for advertising purposes and do not enable these
          third-party advertising or analytics services. This applies
          automatically based on your location and cannot be overridden.
        </p>
      </div>

      <div>
        <h2>Section 9 - Age of Consent</h2>
        <p>
          By using this site, you represent that you are at least the age of
          majority in your state or province of residence, or that you are the
          age of majority in your state or province of residence and you have
          given us your consent to allow any of your minor dependents to use
          this site.
        </p>
      </div>

      <div>
        <h2>Section 10 - Changes to this Privacy Policy</h2>
        <p>
          We reserve the right to modify this privacy policy at any time, so
          please review it frequently. Changes and clarifications will take
          effect immediately upon their posting on the website. If we make
          material changes to this policy, we will notify you here that it has
          been updated, so that you are aware of what information we collect,
          how we use it, and under what circumstances, if any, we use and/or
          disclose it.
        </p>
        <p>
          If our store is acquired or merged with another company, your
          information may be transferred to the new owners so that we may
          continue to sell products to you.
        </p>
      </div>

      <div>
        <h2>Questions and Contact Information</h2>
        <p>
          If you would like to: access, correct, amend or delete any personal
          information we have about you, register a complaint, or simply want
          more information contact our Privacy Compliance Officer at{' '}
          <a href="mailto:support@bestfoodtrucks.com">
            support@bestfoodtrucks.com
          </a>{' '}
          or by mail at Best Food Trucks Inc.
        </p>
      </div>
    </div>
  </PageLayout>
)

const PrivacyPageWithApollo = withApollo({ ssr: true })(PrivacyPage)
export default PrivacyPageWithApollo
